import React, { useMemo, useRef } from "react";
import { Form, Input, Select, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "../address/deliveryAddress.css";
import "../paymentButton/paymentButton.css";
import CustomButton from "../../resuableComponents/CustomButton";
import TranslateText from "../../resuableComponents/TranslateText";
import { blockInvalidChar, buildCustomFieldRules, isEmpty } from "../../../Utils/utils";
import { customFieldEnteredEvent, customFieldSelectedEvent } from "../../../Utils/uiAnalytics";
import { useFieldAnalytics } from "../../../Utils/useFieldAnalytics";
import { selectIsMobileLayout } from "../../../slices/MobileLayoutSlice";
import { openAdditionalInfo, saveAdditionalInfo, updateShopperDetails } from "../../../slices/PaymentSessionDetailsSlice";

const useEnabledCustomFields = () => {
  const allCustomFields = useSelector((state) => state.additionalComponents.customFields);
  return useMemo(() => (allCustomFields || []).filter((field) => field?.enabled === true), [allCustomFields]);
};

function AdditionalInfoForm({ customFields }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const isMobileLayout = useSelector((state) => selectIsMobileLayout(state));
  const savedValues = useSelector((state) => state.paymentSessionDetails.additionalInfoValues);
  const updatedShopperDetails = useSelector((state) => state.paymentSessionDetails.updatedShopperDetails);
  const latestSavedAddress = useSelector((state) => state.paymentSessionDetails.latestSavedAddress);
  const { trackFieldSelected, trackFieldEntered, trackAutofillEntered } = useFieldAnalytics();

  // Stable per-field event callbacks — trackFieldSelected/trackFieldEntered dedupe by
  // function identity, so each custom field needs its own fixed reference to carry
  // its fieldName and code through into eventAttrs.
  const selectedEvents = useRef({});
  const getSelectedEvent = (field) =>
    selectedEvents.current[field.fieldName] ??= () =>
      customFieldSelectedEvent(field.fieldName, field.code);
  const enteredEvents = useRef({});
  const getEnteredEvent = (field) =>
    enteredEvents.current[field.fieldName] ??= (source) =>
      customFieldEnteredEvent(field.fieldName, field.code, source);

  const initialValues = useMemo(() => {
    const restored = {};
    (savedValues || []).forEach((cf) => {
      restored[cf.fieldName] = cf.fieldValue || undefined;
    });
    return restored;
  }, []);

  const onFinish = (values) => {
    const payload = customFields.map((f) => ({ fieldName: f.fieldName, fieldValue: values[f.fieldName] ?? "" }));
    dispatch(saveAdditionalInfo(payload));
    if (isEmpty(updatedShopperDetails)) dispatch(updateShopperDetails({ selectedCardData: latestSavedAddress || {} }));
  };

  return (
    <Form
      requiredMark={false}
      layout="vertical"
      name="additionalInformation"
      form={form}
      initialValues={initialValues}
      onFinish={onFinish}
    >
      <div className="cardPadding" style={{ paddingTop: "8px" }}>
        <div className="addressContainer">
          {customFields.map((field) => (
            <div className="addressBox" key={field.fieldName}>
              <Form.Item
                label={
                  <Typography className="regular14Heading deliveryTitle dynamic-font-family">
                    {field.fieldName}{field.mandatory ? "*" : ""}
                  </Typography>
                }
                name={field.fieldName}
                rules={buildCustomFieldRules(field, t)}
              >
                {field.fieldType === "DROPDOWN" ? (
                  <Select
                    placeholder={field.placeHolderText || field.fieldName}
                    onFocus={() => trackFieldSelected(getSelectedEvent(field))}
                    onChange={(val) => trackFieldEntered(getEnteredEvent(field), val)}
                  >
                    {(field.dropDownOptions || []).map((opt) => (
                      <Select.Option key={opt} value={opt}>{opt}</Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    type={field.fieldType === "NUMERIC" ? "number" : "text"}
                    placeholder={field.placeHolderText || field.fieldName}
                    onKeyDown={field.fieldType === "NUMERIC" ? blockInvalidChar : undefined}
                    onFocus={() => trackFieldSelected(getSelectedEvent(field))}
                    onInput={(e) => trackAutofillEntered(e, getEnteredEvent(field))}
                    onBlur={(e) => trackFieldEntered(getEnteredEvent(field), e.target.value)}
                  />
                )}
              </Form.Item>
            </div>
          ))}
        </div>
      </div>
      {isMobileLayout ? (
        <div className="domestic-mbtn-root">
          <CustomButton htmlType="submit" text={t("CONTINUE")} buttonType={"primary-btn"} block={true} />
        </div>
      ) : (
        <div className="continueButtonDiv">
          <CustomButton
            text={
              <div
                className="dynamic-font-family dynamic-button-text"
                style={{ color: sessionDetails?.merchantDetails?.checkoutTheme?.buttonTextColor }}
              >
                <TranslateText label={"CONTINUE"}>Continue </TranslateText>
              </div>
            }
            buttonType={"primary-btn"}
            padding={"10px 50px"}
            htmlType="submit"
          />
        </div>
      )}
    </Form>
  );
}

function AdditionalInfoSummary({ customFields }) {
  const dispatch = useDispatch();
  const savedValues = useSelector((state) => state.paymentSessionDetails.additionalInfoValues);
  const getSavedValue = (fieldName) => (savedValues || []).find((cf) => cf.fieldName === fieldName)?.fieldValue;

  return (
    <div className="collapseAddressDiv">
      <div className="shopperAddressDiv">
        <div className="addressContainer" style={{ flex: 1 }}>
          {customFields.map((field) => {
            const value = getSavedValue(field.fieldName);
            return (
              <div className="addressBox" key={field.fieldName} style={{ wordBreak: "break-word" }}>
                <Typography className="collapseAddressData dynamic-font-family">
                  {field.fieldName}: {isEmpty(value) ? (
                    <TranslateText label={"NOT_PROVIDED"}>Not provided</TranslateText>
                  ) : value}
                </Typography>
              </div>
            );
          })}
        </div>
        <div className="editIcon" onClick={() => dispatch(openAdditionalInfo())}>
          <img src="/assets/address/edit-contained.svg" alt="edit" />
        </div>
      </div>
    </div>
  );
}

// Rendered only when the backend sends enabled custom fields, and only once the
// address / personal details step is done. Payment options stay locked until the
// card is saved when any of the fields is mandatory (see selectIsAdditionalInfoPending).
export default function AdditionalInformation() {
  const customFields = useEnabledCustomFields();
  const addressCollapse = useSelector((state) => state.paymentSessionDetails.addressCollapse);
  const collapsed = useSelector((state) => state.paymentSessionDetails.additionalInfoCollapsed);
  const isMobileLayout = useSelector((state) => selectIsMobileLayout(state));

  if (!customFields.length || !addressCollapse) return null;

  return (
    <div className="deliveryForm" style={isMobileLayout ? { marginTop: "16px" } : { marginBottom: "24px" }}>
      <div className="deliveryAddressHeader">
        <div className="headerDiv">
          <InfoCircleOutlined />
          <Typography className="deeliveryHeading dynamic-font-family">
            <TranslateText label={"ADDITIONAL_INFORMATION"}>Additional Information</TranslateText>
          </Typography>
        </div>
      </div>
      <div style={{ backgroundColor: "white", borderRadius: "0px 0px 12px 12px", border: "1px solid #f0f0f0", borderTop: "none" }}>
        {collapsed ? <AdditionalInfoSummary customFields={customFields} /> : <AdditionalInfoForm customFields={customFields} />}
      </div>
    </div>
  );
}
